import { useEffect, useMemo, useRef, useState } from "react";
import { actionPrefixes, objectTypes } from "@newsela/snitch";
import { LoadingSpinner } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import useFocusManager from "@/view/utils/useFocusManager";
import shufflePowerWordQuestions from "@/view/utils/shufflePowerWordQuestions";
import getPowerWordsPoints from "@/view/utils/getPowerWordsPoints";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import useSubmitPowerWordsQuestion from "@/utils/useSubmitPowerWordsQuestion";
import useStudentPowerWordQuizGet from "@/assignments/useStudentPowerWordQuizGet";
import ActivityErrorMessage from "../ActivityErrorMessage";
import {
  panelWrapperCss,
  scrollContainerCss,
} from "../ActivityPanel/ActivityPanel.style";
import ItemCount from "../ItemCount";
import NavigationButtons from "../NavigationButtons";
import PanelHeader from "../PanelHeader";
import PowerWordQuizSummary from "../PowerWordQuizSummary";
import PowerWordsQuestion from "../PowerWordsQuestion";
import QuizToggleAnswersButton from "../QuizToggleAnswersButton";
import { containerCss, titleCss } from "./PowerWordsContainer.styles";

const propTypes = {
  closeButtonEventProperties: PropTypes.object,
  data: PropTypes.object,
  inProgressPowerWords: PropTypes.object,
  isPresentModeActive: PropTypes.bool,
  isStudent: PropTypes.bool,
  legacyArticleLevelData: PropTypes.object,
  levelId: PropTypes.string,
  contentId: PropTypes.string,
  onClickCloseButton: PropTypes.func,
  shouldFocusCloseButtonRef: PropTypes.object,
  studentId: PropTypes.number,
  updateinProgressPowerWords: PropTypes.func,
  userProperties: PropTypes.shape({
    activeELALicense: PropTypes.bool,
    isAuthorizedDistrictAdmin: PropTypes.bool,
  }),
};

const PowerWordsContainer = ({
  closeButtonEventProperties,
  data,
  inProgressPowerWords,
  isStudent,

  onClickCloseButton = () => null,
  legacyArticleLevelData,
  shouldFocusCloseButtonRef,
  studentId,
  updateinProgressPowerWords,
  userProperties,
  isPresentModeActive,
  contentId,
}) => {
  const router = useRouter();
  const {
    pathname,
    query,
    query: { levelId, question: questionPosition, powerWordsActivityId },
  } = router;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [numberOfCorrectAnswers, setNumberOfCorrectAnswers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummary, setIsSummary] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [selectedChoiceContentId, setSelectedChoiceContentId] =
    useState(undefined);
  const [
    sanitizedStudentPowerWordQuizData,
    setSanitizedStudentPowerWordQuizData,
  ] = useState(undefined);
  const [correctAnswer, setCorrectAnswer] = useState(undefined);
  const [powerWordQuestions, setPowerWordQuestions] = useState([]);
  const [showAnswerInPresentMode, setShowAnswerInPresentMode] = useState(false);
  const totalStudentPowerWordPoints = useMemo(
    () =>
      numberOfCorrectAnswers > 0
        ? getPowerWordsPoints(data?.questions?.length, numberOfCorrectAnswers)
        : 0,
    [numberOfCorrectAnswers],
  );
  // TODO: Update with logic for preview mode
  const showQuestion = !isNaN(Number(questionPosition));
  const [
    fetchStudentPowerWordQuiz,
    {
      data: studentPowerWordDataArray,
      loading: fetchStudentPowerWordQuizLoading,
    },
  ] = useStudentPowerWordQuizGet({
    article_id: legacyArticleLevelData?.id,
    student_id: studentId,
  });
  const isPowerWordActivity = !!powerWordsActivityId;
  const isPreviewMode = !isStudent;
  const questionCounterTitleRef = useRef();
  const errorRef = useRef();
  const { focusElement } = useFocusManager();
  const [handleSubmitPowerWordsQuestion] = useSubmitPowerWordsQuestion();

  const backButtonEventProperties = {
    objectType: objectTypes.articleWord,
    actionPrefix: actionPrefixes.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to Activity Picker from power words activity",
      content_id: levelId,
    },
  };

  /**
   *  This function is in charge to cleaning up the power word quiz's state.
   */
  const clearPowerQuizStatusLevelOnLevelChange = () => {
    setSelectedChoiceContentId(undefined);
    setCorrectAnswer(undefined);
    setNumberOfCorrectAnswers(0);
    setIsSubmitted(false);
  };

  /**
   * This function is in charge to return the other option id if the user selects the wrong one
   */
  const getChoiceContentIdForRightAnswer = ({ choiceId }) => {
    try {
      const question = powerWordQuestions?.[questionPosition - 1];
      const [option] = question?.options?.filter(
        (option) => option.id !== choiceId,
      );
      return option.id;
    } catch (_) {
      return undefined;
    }
  };

  /**
   * Return sanitized data received from the API to create a data structure that matches the data needed on the power word quiz summary page
   */
  const sanitizeStudentPowerWordQuizData = () => {
    const sanitizedData = [];
    studentPowerWordDataArray?.forEach((res) => {
      if (
        sanitizedData.some(
          (item) => item?.headword === res?.question?.article_word?.headword,
        )
      ) {
        const matchIndex = sanitizedData.findIndex(
          (el) => el.headword === res?.question?.article_word?.headword,
        );
        sanitizedData[matchIndex] = {
          ...sanitizedData[matchIndex],
          isCorrect: [...sanitizedData[matchIndex].isCorrect, res?.is_correct],
        };
      } else {
        sanitizedData.push({
          headword: res?.question?.article_word?.headword,
          definition: res?.question?.article_word?.definition,
          isCorrect: [res?.is_correct],
        });
      }
    });
    setSanitizedStudentPowerWordQuizData(sanitizedData);
    return sanitizedData;
  };

  /**
   *  This function is in charge to handle the state correctly of the student's already answered question.
   */
  const handleQuestionAlreadySubmitted = (questionsAnsweredLength) => {
    const {
      is_correct: isCorrectAnswer,
      answer_choice: { content_id: studentAnswerOnCurrentQuestion },
    } = studentPowerWordDataArray[questionsAnsweredLength - 1];
    const totalCorrectQuestionsSoFar = studentPowerWordDataArray.filter(
      (question) => question.is_correct,
    ).length;
    setSelectedChoiceContentId(studentAnswerOnCurrentQuestion);
    const rightAnswerContentId = isCorrectAnswer
      ? studentAnswerOnCurrentQuestion
      : getChoiceContentIdForRightAnswer({
          choiceId: studentAnswerOnCurrentQuestion,
        });
    setCorrectAnswer(rightAnswerContentId);
    setNumberOfCorrectAnswers(totalCorrectQuestionsSoFar);
    setIsSubmitted(true);
  };

  const onClickBackButton = () => {
    const query = { ...router.query };
    // Remove all query params associated with the power words.
    delete query.activityPanel;
    delete query.question;
    delete query.powerWordsActivityId;
    // Without the power words query params, the user will return to the activity picker.
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const sanitizePowerWordsQuestionPayload = () => ({
    answer_choice_content_id: selectedChoiceContentId,
    date_answered: new Date().toISOString(),
  });

  const handleCurrentlyStudentPoints = (isCorrectAnswer) =>
    isCorrectAnswer ? numberOfCorrectAnswers + 1 : numberOfCorrectAnswers;

  /**
   * This function is in charge of submitting the student's current choice and handling the state based on the returned server data
   */
  const submitPowerWordsQuestion = async () => {
    if (!selectedChoiceContentId) {
      const errorEmptySelection = {
        hasError: true,
        message: "Select an answer",
      };

      setErrorMessage((errorMessage) => ({
        ...errorMessage,
        ...errorEmptySelection,
      }));

      return;
    }

    try {
      if (!isSubmitted) {
        setIsSubmitted(true);
        const payload = sanitizePowerWordsQuestionPayload();
        const {
          data: { is_correct: isCorrectAnswer },
        } = await handleSubmitPowerWordsQuestion(payload);
        const rightAnswerContentId = isCorrectAnswer
          ? selectedChoiceContentId
          : getChoiceContentIdForRightAnswer({
              choiceId: selectedChoiceContentId,
            });
        setCorrectAnswer(rightAnswerContentId);
        const currentlyStudentPoints =
          handleCurrentlyStudentPoints(isCorrectAnswer);
        setNumberOfCorrectAnswers(currentlyStudentPoints);
      }
      setErrorMessage(undefined);
    } catch (_) {
      setIsSubmitted(false);
      const errorSomethingWentWrong = {
        hasError: true,
        message: "Something went wrong. Please try to resubmit your answer.",
      };

      setErrorMessage((errorMessage) => ({
        ...errorMessage,
        ...errorSomethingWentWrong,
      }));

      setIsSubmitted(false);
    } finally {
      questionCounterTitleRef.current?.focus();
    }
  };

  const handleReturnToActivities = () => {
    const query = { ...router.query };
    delete query.powerWordsActivityId;
    delete query.question;
    delete query.activityPanel;
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const handleNonSubmittedState = () => {
    setErrorMessage(undefined);
    setSelectedChoiceContentId(undefined);
    setCorrectAnswer(undefined);
    setIsSubmitted(false);
  };

  const refetchStudentPowerWordQuiz = async () => {
    try {
      setIsLoading(true);
      await fetchStudentPowerWordQuiz();
      setIsLoading(false);
    } catch (_) {
      setIsLoading(false);
      // TODO: handle error exception
    }
  };

  const onClickNavigationButtons = (navigateTo) => {
    if (isPresentModeActive) {
      setShowAnswerInPresentMode(false);
    }

    const href = {
      pathname,
      query: {
        ...query,
        question:
          navigateTo === "continue" || navigateTo === "next"
            ? parseInt(questionPosition) + 1
            : navigateTo === "back"
              ? parseInt(questionPosition) - 1
              : null,
      },
    };

    if (navigateTo !== "submit") handleNonSubmittedState();

    switch (navigateTo) {
      case "continue":
      case "next":
      case "back":
        router.replace(href, null, { shallow: true });
        break;
      case "submit":
        submitPowerWordsQuestion();
        break;
      case "summary":
        refetchStudentPowerWordQuiz();
        setIsSummary(true);
        break;
      case "returnToActivities":
        handleReturnToActivities();
        break;
      default:
        href.query.question = 1;
        router.replace(href, null, { shallow: true });
    }
  };

  const setPowerWordsChoiceSelection = (choiceContentId) => {
    const questionId = powerWordQuestions?.[questionPosition - 1].id;

    const choiceSelection = {
      [questionId]: {
        selectedAnswer: choiceContentId,
      },
    };

    setSelectedChoiceContentId(choiceContentId);
    updateinProgressPowerWords({
      ...inProgressPowerWords,
      [data.id]: {
        powerWordsQuestions: {
          ...inProgressPowerWords[data.id]?.powerWordsQuestions,
          ...choiceSelection,
        },
      },
    });
  };

  /**
   * Attempt to fetch the student's completed power word quiz
   * each time the level id changes.
   */
  useEffect(() => {
    isStudent && clearPowerQuizStatusLevelOnLevelChange();
    isStudent && fetchStudentPowerWordQuiz();
  }, [legacyArticleLevelData]);

  useEffect(
    () => {
      if (!studentPowerWordDataArray) {
        return setPowerWordQuestions(data?.questions);
      }
      setPowerWordQuestions(
        shufflePowerWordQuestions(data?.questions, studentPowerWordDataArray),
      );
    },
    [JSON.stringify(studentPowerWordDataArray)],
    questionPosition,
  );

  /**
   * If a completed student quiz has been returned, redirect to the user to
   * summary screen. If the user is already on the summary screen,
   * redirect the user to the first quiz question. The user remains on the same numbered question otherwise
   */
  useEffect(() => {
    try {
      setIsLoading(true);
      const href = { pathname, query: { ...query } };
      if (studentPowerWordDataArray && data?.questions) {
        sanitizeStudentPowerWordQuizData();
        const questionsAnsweredLength = studentPowerWordDataArray?.length;
        const allQuestionsWereAnswered =
          questionsAnsweredLength === powerWordQuestions?.length;

        if (allQuestionsWereAnswered) {
          delete href.query.question;
          setIsSummary(true);
        } else if (questionsAnsweredLength > 0) {
          setIsSummary(false);
          href.query.question = questionsAnsweredLength;
          handleQuestionAlreadySubmitted(questionsAnsweredLength);
        } else {
          setIsSummary(false);
          href.query.question = 1;
        }
        router.replace(href, null, { shallow: false });
      } else if (isPreviewMode) {
        href.query.question = 1;
        router.replace(href, null, { shallow: false });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    JSON.stringify(studentPowerWordDataArray),
    JSON.stringify(data?.questions),
    JSON.stringify(powerWordQuestions),
    !!questionPosition,
  ]);

  /**
   * This useEffect will help update the question answers when the user navigates from one question to another to ensure we're pull the correct data
   * Example scenario: User finishes question and goes from question 1 to question 2 (Not completed). If the user refreshes the page, our component will force them to back to question 1.
   * Example scenario #2: User has only completed 2 questions and directly lands on a page where question > 2.
   */
  useEffect(() => {
    const positionMatchesAnswerResponseCount =
      parseInt(questionPosition) === studentPowerWordDataArray?.length;
    if (
      !studentPowerWordDataArray ||
      !questionPosition ||
      !positionMatchesAnswerResponseCount
    )
      return;
    handleQuestionAlreadySubmitted(studentPowerWordDataArray?.length);
  }, [JSON.stringify(studentPowerWordDataArray), questionPosition]);

  /**
   * When a change is detected in the 'question' param, focus will be set on
   * the back button in the panel header
   */
  useEffect(() => {
    focusElement(focusElementRefKeys.activityPanelBackButton);
  }, [router.query.question]);

  const renderPowerWordQuizSummary = () => {
    return isSummary ? (
      <>
        <div css={[containerCss, scrollContainerCss]}>
          <PowerWordQuizSummary
            sanitizedStudentPowerWordQuizData={
              sanitizedStudentPowerWordQuizData
            }
            contentId={contentId}
          />
        </div>
        <NavigationButtons>
          <NavigationButtons.PowerWordsNavigationButtons
            currentQuestion={questionPosition}
            handleClick={onClickNavigationButtons}
            isStudent={isStudent}
            isSubmitted={isSubmitted}
            isSummary={isSummary}
            objectId={powerWordsActivityId}
            contentId={levelId}
            questionCount={powerWordQuestions.length}
          />
        </NavigationButtons>
      </>
    ) : null;
  };

  const renderPowerWordsElements = () => {
    const currentQuestion = powerWordQuestions[questionPosition - 1];
    if (fetchStudentPowerWordQuizLoading || isLoading || !powerWordQuestions) {
      return <LoadingSpinner />;
    }

    return (
      <>
        {renderPowerWordQuizSummary()}
        {showQuestion && data && !isSummary ? (
          <>
            <div css={[containerCss, scrollContainerCss]} tabIndex={0}>
              <ItemCount
                count={powerWordQuestions.length}
                currentItem={questionPosition}
                isCompact={isPreviewMode}
                isSubmitted={isSubmitted}
                hasScore={isPowerWordActivity && !isPreviewMode}
                powerWordsPoints={totalStudentPowerWordPoints}
                questionCounterTitleRef={questionCounterTitleRef}
                userProperties={userProperties}
              />
              <PowerWordsQuestion
                contentId={data.id}
                correctAnswer={correctAnswer}
                currentLevelId={levelId}
                isPreviewMode={isPreviewMode}
                isStudent={isStudent}
                isSubmitted={isSubmitted}
                question={currentQuestion}
                selectedChoiceContentId={selectedChoiceContentId}
                selectedAnswers={inProgressPowerWords}
                powerWordsActivityId={powerWordsActivityId}
                updateSelectedAnswers={setPowerWordsChoiceSelection}
                isPresentModeActive={isPresentModeActive}
                showAnswerInPresentMode={showAnswerInPresentMode}
              />
              {errorMessage?.hasError && (
                <ActivityErrorMessage
                  message={errorMessage?.message}
                  ref={errorRef}
                />
              )}
              {isPresentModeActive ? (
                <QuizToggleAnswersButton
                  showAnswersInPresentMode={showAnswerInPresentMode}
                  questionId={currentQuestion?.id}
                  contentId={levelId}
                  handleOnClick={() =>
                    setShowAnswerInPresentMode(
                      (showAnswerInPresentMode) => !showAnswerInPresentMode,
                    )
                  }
                />
              ) : null}
            </div>
            <NavigationButtons>
              <NavigationButtons.PowerWordsNavigationButtons
                currentQuestion={questionPosition}
                handleClick={onClickNavigationButtons}
                isStudent={isStudent}
                isPreviewMode={isPreviewMode}
                isSubmitted={isSubmitted}
                objectId={powerWordsActivityId}
                contentId={levelId}
                questionCount={powerWordQuestions.length}
              />
            </NavigationButtons>
          </>
        ) : null}
      </>
    );
  };

  return (
    <div css={panelWrapperCss}>
      <PanelHeader
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <h2 css={titleCss}>Power Words</h2>
      </PanelHeader>
      {renderPowerWordsElements()}
    </div>
  );
};

PowerWordsContainer.propTypes = propTypes;

export default PowerWordsContainer;
